import { createContext, useCallback, useState } from 'react';

import cookie from 'cookie';

import { useSentryLogger } from '@App/hooks/log';
import { preSignup } from '@App/requests/preSignup';

import {
    confirmOtp,
    logout,
    refreshTokens,
    requestOtp,
    signUp,
} from '../requests/auth';
import { updateClientId } from '../requests/updateClientId';

type AuthContextProps = {
    loading: boolean;
    setLoading: Function;
    loggedIn: boolean;
    isToFetchApplicationData: boolean | null;
    requestOTP: Function;
    confirmOTP: Function;
    logOut: Function;
    register: Function;
    isLoggedIn: Function;
    getAccessToken: Function;
    updateFetchApplicationDataFlag: Function;
    getIdToken: Function;
};

export enum AuthErrors {
    INCORRECT_CODE = 'Code not recognised, please double check and try again.',
    CODE_EXPIRED = 'Code expired, please request a new one above.',
}

export enum AuthErrorType {
    USER_EXISTS = 'USER_EXISTS',
    INCORRECT_CODE = 'INCORRECT_CODE',
    CODE_EXPIRED = 'CODE_EXPIRED',
}

export const AuthContext = createContext<AuthContextProps>({
    loading: true,
    setLoading: () => {},
    loggedIn: false,
    isToFetchApplicationData: null,
    requestOTP: () => {},
    confirmOTP: () => {},
    logOut: () => {},
    register: () => {},
    isLoggedIn: () => {},
    getAccessToken: () => {},
    updateFetchApplicationDataFlag: () => {},
    getIdToken: () => {},
});

const authError = (
    error: any,
    customMessage?: string,
    applicationId?: string,
    errorType?: AuthErrorType,
    session?: string,
) => ({
    success: false,
    message: customMessage || error,
    errorType,
    applicationId: applicationId,
    session: session,
});

const authSuccess = (
    customMessage?: string,
    applicationId?: string,
    session?: string,
) => ({
    success: true,
    message: customMessage || '',
    applicationId: applicationId,
    session: session,
});

const AuthProvider = (props: { children: any }) => {
    const [user, setUser] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [isToFetchApplicationData, setIsToFetchApplicationData] =
        useState<boolean>(false);

    const { sentryError, flushLogs } = useSentryLogger(
        'Authentication',
        'Auth context',
    );

    const handleError = useCallback(
        (
            error: any,
            section?: string,
            applicationId?: string,
            session?: string,
        ) => {
            setLoading(false);

            const errorType = error?.errorType;

            if (errorType === AuthErrorType.USER_EXISTS) {
                return authError(
                    error.errorMessage,
                    undefined,
                    applicationId,
                    errorType,
                );
            }

            if (errorType === AuthErrorType.INCORRECT_CODE) {
                return authError(
                    error.errorMessage,
                    AuthErrors.INCORRECT_CODE,
                    applicationId,
                    errorType,
                    session,
                );
            }

            if (errorType === AuthErrorType.CODE_EXPIRED) {
                return authError(
                    error.errorMessage,
                    AuthErrors.CODE_EXPIRED,
                    applicationId,
                    errorType,
                );
            }

            sentryError(`error on ${section}`, error);
            flushLogs();

            return authError(
                error,
                "Looks like we've had a technical issue.\nPlease try again.",
                applicationId,
            );
        },
        [flushLogs, sentryError],
    );

    const register = useCallback(
        async (email: string, applicationId: string) => {
            setLoading(true);

            let response;
            email = email.toLowerCase();
            try {
                response = await preSignup(applicationId, email);
                const user = await signUp(email);

                localStorage.setItem('user.email', email);
                localStorage.setItem(
                    'user.applicationId',
                    applicationId || response.applicationId,
                );

                setUser(true);

                // needed to get the email in podipo
                document.cookie = cookie.serialize('user_email', email, {
                    path: '/',
                    secure: true,
                    httpOnly: false,
                    sameSite: 'none',
                    domain: window._env_.SELINA_DOMAIN,
                });

                setAuthCookies(
                    user.data.authResponse.idToken,
                    user.data.authResponse.accessToken,
                    user.data.authResponse.refreshToken,
                );

                if (applicationId) {
                    await updatesClientId(applicationId);
                }

                setLoading(false);
                setIsToFetchApplicationData(true);

                return authSuccess(
                    'Registration was successful.',
                    response.applicationId,
                );
            } catch (e: any) {
                const errorData = e.response
                    ? { ...e.response.data, status: e.response.status }
                    : e;
                return handleError(
                    errorData,
                    'Registration',
                    response?.applicationId,
                );
            } finally {
                setLoading(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [handleError],
    );

    const requestOTP = useCallback(
        async (email: string) => {
            setLoading(true);
            email = email.toLowerCase();
            try {
                const user = await requestOtp(email);

                return authSuccess(
                    'Successful OTP request',
                    undefined,
                    user.data.session,
                );
            } catch (e: any) {
                const errorData = e.response
                    ? { ...e.response.data, status: e.response.status }
                    : e;

                return handleError(errorData, 'OTP request');
            } finally {
                setLoading(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [handleError],
    );

    const confirmOTP = useCallback(
        async (
            email: string,
            otp: string,
            applicationId: string,
            session: string,
        ) => {
            setLoading(true);
            email = email.toLowerCase();
            try {
                const user = await confirmOtp(email, session, otp);

                // user is allowed 3 attempts, first 2 dont trigger BE error
                if (Number(user.data.parameter?.attemptsLeft) > 0) {
                    const errorData = {
                        errorType: AuthErrorType.INCORRECT_CODE,
                        errorMessage: AuthErrors.INCORRECT_CODE,
                    };
                    return handleError(
                        errorData,
                        'Login',
                        applicationId,
                        user.data.session,
                    );
                }

                localStorage.setItem('user.email', email);
                localStorage.setItem('user.applicationId', applicationId);

                setUser(true);

                // needed to get the email in podipo
                document.cookie = cookie.serialize('user_email', email, {
                    path: '/',
                    secure: true,
                    httpOnly: false,
                    sameSite: 'none',
                    domain: window._env_.SELINA_DOMAIN,
                });

                setAuthCookies(
                    user.data.idToken,
                    user.data.accessToken,
                    user.data.refreshToken,
                );

                if (applicationId) {
                    await updatesClientId(applicationId);
                }

                setLoading(false);
                setIsToFetchApplicationData(true);

                return authSuccess('Successful login');
            } catch (e: any) {
                if (e.response?.status === 440) {
                    const errorData = {
                        errorType: AuthErrorType.CODE_EXPIRED,
                        errorMessage: AuthErrors.CODE_EXPIRED,
                    };
                    return handleError(errorData, 'Login');
                }

                const errorData = e.response
                    ? { ...e.response.data, status: e.response.status }
                    : e;

                return handleError(errorData, 'Login');
            } finally {
                setLoading(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [handleError],
    );

    const parseJwt = (token: string) => {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            return JSON.parse(atob(base64));
        } catch (error) {
            return null;
        }
    };

    const setAuthCookies = useCallback(
        (idToken: string, accessToken: string, refreshToken: string) => {
            const cookies = [
                { name: 'idToken', value: idToken },
                {
                    name: 'accessToken',
                    value: accessToken,
                },
                {
                    name: 'refreshToken',
                    value: refreshToken,
                },
            ];

            cookies.forEach(({ name, value }) => {
                document.cookie = cookie.serialize(name, value, {
                    path: '/',
                    secure: true,
                    httpOnly: false,
                    sameSite: 'none',
                    domain: window._env_.SELINA_DOMAIN,
                });
            });
        },
        [],
    );

    const clearAuthCookies = () => {
        ['idToken', 'accessToken', 'refreshToken'].forEach((name) => {
            document.cookie = cookie.serialize(name, '', {
                path: '/',
                secure: true,
                httpOnly: false,
                sameSite: 'none',
                domain: window._env_.SELINA_DOMAIN,
                expires: new Date(0),
            });
        });
    };

    const logOut = useCallback(async () => {
        setLoading(true);
        try {
            localStorage.removeItem('user.email');
            localStorage.removeItem('user.applicationId');
            setIsToFetchApplicationData(false);

            const cookies = cookie.parse(document.cookie);
            const { accessToken, refreshToken } = cookies;

            clearAuthCookies();

            if (accessToken && refreshToken) {
                await logout(accessToken, refreshToken);
            }
        } catch (e) {
            setLoading(false);
            return authError(e);
        } finally {
            setLoading(false);
            setUser(null);
        }
    }, []);

    const updatesClientId = async (id: string) => {
        try {
            await updateClientId(
                await getAccessToken(),
                await getIdToken(),
                id,
            );
        } catch (e) {
            return handleError(e, 'update client ID');
        }
    };

    const refreshAndStoreTokens = useCallback(
        async (refreshToken: string) => {
            try {
                const newTokens = await refreshTokens(refreshToken);
                if (newTokens.data) {
                    setAuthCookies(
                        newTokens.data.idToken,
                        newTokens.data.accessToken,
                        newTokens.data.refreshToken,
                    );
                    return newTokens.data;
                }
            } catch (e) {
                return null;
            }
        },
        [setAuthCookies],
    );

    const isLoggedIn = useCallback(async () => {
        setLoading(true);

        const cookies = cookie.parse(document.cookie);
        const accessToken = cookies.accessToken;
        const refreshToken = cookies.refreshToken;

        if (!accessToken) {
            setLoading(false);
            return false;
        }

        const tokenData = parseJwt(accessToken);
        const currentTime = Math.floor(Date.now() / 1000);

        if (tokenData && tokenData.exp > currentTime) {
            setLoading(false);
            setUser(true);
            return true;
        }

        if (!refreshToken) {
            setLoading(false);
            return false;
        }

        const newTokens = await refreshAndStoreTokens(refreshToken);
        if (!newTokens) {
            setLoading(false);
            return false;
        }

        setLoading(false);
        setUser(true);
        return true;
    }, [refreshAndStoreTokens]);

    const getAccessToken = useCallback(async () => {
        try {
            const cookies = cookie.parse(document.cookie);
            let accessToken = cookies.accessToken;

            if (!accessToken) {
                return authError('Access token is missing.');
            }

            const tokenData = parseJwt(accessToken);
            const currentTime = Math.floor(Date.now() / 1000);

            if (!tokenData || tokenData.exp < currentTime) {
                const newTokens = await refreshAndStoreTokens(
                    cookies.refreshToken,
                );

                if (!newTokens) {
                    return authError('Failed to refresh access token.');
                }

                accessToken = newTokens.accessToken;
            }

            return accessToken;
        } catch (e) {
            setLoading(false);
            return authError(e);
        }
    }, [refreshAndStoreTokens]);

    const getIdToken = useCallback(async () => {
        try {
            const cookies = cookie.parse(document.cookie);
            let idToken = cookies.idToken;

            if (!idToken) {
                return authError('ID token is missing.');
            }

            const tokenData = parseJwt(idToken);
            const currentTime = Math.floor(Date.now() / 1000);

            if (!tokenData || tokenData.exp < currentTime) {
                const newTokens = await refreshAndStoreTokens(
                    cookies.refreshToken,
                );

                if (!newTokens) {
                    return authError('Failed to refresh ID token.');
                }

                idToken = newTokens.idToken;
            }

            return idToken;
        } catch (e) {
            setLoading(false);
            return authError(e);
        }
    }, [refreshAndStoreTokens]);

    const updateFetchApplicationDataFlag = useCallback(async () => {
        const response = await isLoggedIn();
        setIsToFetchApplicationData(!!response);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <AuthContext.Provider
            value={{
                loading,
                setLoading,
                loggedIn: !!user,
                requestOTP,
                confirmOTP,
                logOut,
                register,
                isLoggedIn,
                getAccessToken,
                isToFetchApplicationData,
                updateFetchApplicationDataFlag,
                getIdToken,
            }}
        >
            {props.children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;
